import type {
  ResultListState,
  ResultListStateQuery,
} from '../state/state.types';
import type {
  FilterItem,
  FilterSection,
  LatLonPair,
  Metadata,
  SearchField,
  VoyagerDisplayConfig,
} from '../models/search-result';
import { initialSearchState } from './initialState';
import type { VoyagerSearchContextState } from './searchContext.types';

export type VoyagerSearchAction =
  | { type: 'UPDATE_SEARCH_QUERY'; payload: string }
  | { type: 'UPDATE_SEARCH_FIELDS'; payload: SearchField[] }
  | { type: 'UPDATE_FILTERS'; payload: FilterItem[] }
  | { type: 'UPDATE_BBOX'; payload: LatLonPair[] }
  | {
      type: 'UPDATE_PAGINATION';
      payload: { pageIndex: number; pageSize: number };
    }
  | { type: 'UPDATE_SORT_BY'; payload: string }
  | { type: 'UPDATE_SORT_ORDER'; payload: 'asc' | 'desc' }
  | {
      type: 'UPDATE_SEARCH_SCOPE';
      payload: string;
    }
  | { type: 'UPDATE_ACTIVE_FILTER'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SEARCH_RESULTS'; payload: ResultListState }
  | { type: 'SET_QUERY'; payload: ResultListStateQuery }
  | { type: 'SET_FACETS'; payload: FilterSection[] }
  | { type: 'SET_HEATMAP'; payload: any }
  | { type: 'SET_HOVERED_ITEM'; payload: Metadata | null }
  | { type: 'SET_DISPLAY_CONFIG'; payload: VoyagerDisplayConfig | null }
  | { type: 'CLEAR_SEARCH' };

export const voyagerSearchReducer = (
  state: VoyagerSearchContextState,
  action: VoyagerSearchAction,
): VoyagerSearchContextState => {
  switch (action.type) {
    case 'UPDATE_SEARCH_QUERY':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: {
            ...state.searchState.query!,
            search: action.payload,
            pageIndex: 0,
          },
        },
      };

    case 'UPDATE_BBOX':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: {
            ...state.searchState.query!,
            spatialFilter: {
              bbox: action.payload,
              isChecked: action.payload.length > 0,
            },
            pageIndex: 0,
          },
        },
      };

    case 'UPDATE_SEARCH_FIELDS':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: {
            ...state.searchState.query!,
            settings: {
              searchFields: action.payload,
            },
            pageIndex: 0,
          },
        },
      };

    case 'UPDATE_FILTERS':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: {
            ...state.searchState.query!,
            filters: action.payload,
            pageIndex: 0,
          },
        },
      };

    case 'UPDATE_PAGINATION':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: {
            ...state.searchState.query!,
            pageIndex: action.payload.pageIndex,
            pageSize: action.payload.pageSize,
          },
        },
      };

    case 'UPDATE_SORT_BY':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: {
            ...state.searchState.query!,
            sortBy: action.payload,
            pageIndex: 0,
          },
        },
      };

    case 'UPDATE_SORT_ORDER':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: {
            ...state.searchState.query!,
            sortOrder: action.payload,
            pageIndex: 0,
          },
        },
      };

    case 'UPDATE_SEARCH_SCOPE':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: {
            ...state.searchState.query!,
            searchScope: action.payload,
            pageIndex: 0,
          },
        },
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
        searchState: {
          ...state.searchState,
          isLoading: action.payload,
        },
      };

    case 'SET_SEARCH_RESULTS':
      return {
        ...state,
        searchState: action.payload,
        isLoading: false,
      };

    case 'SET_FACETS':
      return {
        ...state,
        facets: action.payload,
      };

    case 'SET_HEATMAP':
      return {
        ...state,
        heatmapData: action.payload,
      };

    case 'SET_HOVERED_ITEM':
      return {
        ...state,
        hoveredItem: action.payload,
      };

    case 'SET_DISPLAY_CONFIG':
      return {
        ...state,
        displayConfig: action.payload,
      };

    case 'CLEAR_SEARCH':
      return {
        ...state,
        searchState: {
          ...initialSearchState,
          query: {
            ...initialSearchState.query!,
            settings: state.searchState.query?.settings || {
              searchFields: ['any'],
            },
          },
        },
      };

    case 'UPDATE_ACTIVE_FILTER':
      return {
        ...state,
        activeFilter: action.payload,
      };

    case 'SET_QUERY':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          query: action.payload,
        },
      };

    default:
      return state;
  }
};
